import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FamilyExplorer } from "./FamilyExplorer";
import { ProphecyWheel } from "./ProphecyWheel";
import "./ProphetsInsight.css";

/**
 * ProphetsInsight
 * -------------------------------------------------------------------
 * One large SHARED frame that hosts three panels; only the inner
 * content slides horizontally while the frame stays visually stable.
 *
 *   Panel 1 — THE PROPHECY          (lore text, redesigned inside the
 *                                    shared frame with a subtle title
 *                                    scan-sweep)
 *   Panel 2 — DISCOVER THE FAMILIES (renders <FamilyExplorer />)
 *   Panel 3 — REVEAL YOUR PROPHECY  (renders <ProphecyWheel />)
 *
 * Navigation: left/right arrows attached to the frame, pagination
 * dots below the frame, CTA buttons on Panels 1 & 2, horizontal
 * swipe/drag, and ← / → keyboard arrows.
 *
 * REVERSIBILITY: delete this component + its .css and restore the
 * previous <ProphetsLore /> + <ProphecyWheel /> lines in
 * ProphetsHome.jsx. `ProphetsLore.jsx` and `ProphecyWheel.jsx` are
 * untouched on disk.
 * -------------------------------------------------------------------
 */

const PANEL_COUNT = 3;
const DRAG_THRESHOLD = 60;         // px to trigger a slide change
const DRAG_CLICK_SUPPRESS_PX = 6;  // px to flag "was dragging"
const HORIZONTAL_DOMINANCE = 1.4;  // dx must be > 1.4× dy to count as swipe

export function ProphetsInsight() {
  const rootRef = useRef(null);
  const [active, setActive] = useState(0);
  const [familyAccent, setFamilyAccent] = useState("#338cff"); // default = The Oracles blue
  const dragRef = useRef({ startX: 0, dragging: false });
  const wasDragRef = useRef(false);

  // Called by <FamilyExplorer> on every family switch so the panel-level
  // "Reveal Your Prophecy" CTA can adapt its accent to the active family.
  const onActiveFamilyChange = useCallback((fam) => {
    if (fam && typeof fam.accent === "string") setFamilyAccent(fam.accent);
  }, []);

  const clamp = useCallback(
    (idx) => Math.max(0, Math.min(PANEL_COUNT - 1, idx)),
    [],
  );
  const goTo = useCallback((idx) => setActive(clamp(idx)), [clamp]);
  const go = useCallback((delta) => setActive((a) => clamp(a + delta)), [clamp]);

  // Keyboard arrow navigation on desktop.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft")  { e.preventDefault(); go(-1); }
      if (e.key === "ArrowRight") { e.preventDefault(); go(+1); }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [go]);

  // ------------ pointer drag / swipe ------------
  const onPointerDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, dragging: true };
    wasDragRef.current = false;
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - (dragRef.current.startY || 0);
    if (Math.abs(dx) > DRAG_CLICK_SUPPRESS_PX || Math.abs(dy) > DRAG_CLICK_SUPPRESS_PX) {
      wasDragRef.current = true;
    }
  };
  const onPointerUp = (e) => {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - (dragRef.current.startY || 0);
    dragRef.current.dragging = false;
    // Only trigger a slide when the gesture is BOTH large enough AND
    // clearly horizontal (dx dominates over dy). This prevents vertical
    // page scrolls and diagonal drifts from accidentally paginating.
    if (Math.abs(dx) > DRAG_THRESHOLD && Math.abs(dx) > Math.abs(dy) * HORIZONTAL_DOMINANCE) {
      go(dx < 0 ? +1 : -1);
    }
    if (wasDragRef.current) {
      setTimeout(() => { wasDragRef.current = false; }, 0);
    }
  };

  // Every affordance (CTA, dot, arrow) funnels into this one function.
  const onNavigate = useCallback(
    (idx) => {
      if (wasDragRef.current) return;
      goTo(idx);
    },
    [goTo],
  );

  const atStart = active === 0;
  const atEnd   = active === PANEL_COUNT - 1;

  return (
    <section
      id="lore"
      ref={rootRef}
      tabIndex={0}
      aria-roledescription="carousel"
      aria-label="Prophets Insight — three-panel section"
      data-testid="prophets-insight"
      className="prophets-insight relative w-full outline-none"
    >
      {/* Ambient orbital echo behind the whole section (subtle) */}
      <div aria-hidden="true" className="prophets-insight-backdrop">
        <svg viewBox="0 0 1600 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <radialGradient id="insight-halo" cx="50%" cy="55%" r="50%">
              <stop offset="0%"  stopColor="#4d5a7a" stopOpacity="0.08" />
              <stop offset="70%" stopColor="#000000" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="insight-orbit" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="rgba(220,228,244,0)" />
              <stop offset="50%"  stopColor="rgba(220,228,244,0.10)" />
              <stop offset="100%" stopColor="rgba(220,228,244,0)" />
            </linearGradient>
          </defs>
          <rect width="1600" height="800" fill="url(#insight-halo)" />
          <ellipse cx="800" cy="400" rx="720" ry="180"
                   fill="none" stroke="url(#insight-orbit)" strokeWidth="0.7" />
          <ellipse cx="800" cy="400" rx="540" ry="130"
                   fill="none" stroke="url(#insight-orbit)" strokeWidth="0.6" opacity="0.8" />
        </svg>
      </div>

      {/* ---------------- SHARED FRAME ----------------
          One large premium frame that stays visually stable while the
          inner track slides between panels. Reuses the exact glass /
          border language already used by the FamilyExplorer frame. */}
      <div
        className="prophets-insight-frame"
        data-testid="insight-frame"
        data-panel={active + 1}
      >
        {/* Four elegant corner accents */}
        <span aria-hidden="true" className="prophets-insight-corner prophets-insight-corner--tl" />
        <span aria-hidden="true" className="prophets-insight-corner prophets-insight-corner--tr" />
        <span aria-hidden="true" className="prophets-insight-corner prophets-insight-corner--bl" />
        <span aria-hidden="true" className="prophets-insight-corner prophets-insight-corner--br" />

        {/* Left / right navigation arrows attached to the frame */}
        <button
          type="button"
          aria-label="Previous panel"
          data-testid="insight-arrow-prev"
          onClick={() => onNavigate(active - 1)}
          disabled={atStart}
          className={`prophets-insight-arrow prophets-insight-arrow--left ${atStart ? "is-disabled" : ""}`}
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <button
          type="button"
          aria-label="Next panel"
          data-testid="insight-arrow-next"
          onClick={() => onNavigate(active + 1)}
          disabled={atEnd}
          className={`prophets-insight-arrow prophets-insight-arrow--right ${atEnd ? "is-disabled" : ""}`}
        >
          <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
        </button>

        {/* Sliding track — only the content moves, frame stays fixed. */}
        <div
          className="prophets-insight-track"
          style={{ transform: `translateX(-${active * 100}%)` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* ---------------- Panel 1 — The Prophecy ---------------- */}
          <div
            className={`prophets-insight-panel prophets-insight-panel--1 prophets-lore is-in-view ${active === 0 ? "is-showing" : ""}`}
            data-testid="insight-panel-1"
            role="tabpanel"
            aria-labelledby="insight-dot-0"
            aria-hidden={active !== 0}
          >
            {/* Subtle intermittent ambient scan across the whole panel */}
            <span aria-hidden="true" className="prophets-insight-panel-scan" />

            <div className="prophets-insight-panel-inner">
              {/* Left column — text block (left-aligned, vertically centered) */}
              <div className="prophets-insight-prophecy-text">
                <h2
                  data-testid="prophecy-title"
                  className="p-nav prophets-insight-title"
                >
                  <span className="prophets-insight-title-line">The</span>
                  <span className="prophets-insight-title-line">Prophecy</span>
                  <span aria-hidden="true" className="prophets-insight-title-sweep" />
                </h2>

                <div className="prophets-insight-lede">
                  Humanity did not disappear. It evolved.
                </div>

                <div className="prophets-insight-body">
                  <p>
                    Shaped by artificial intelligence, genetic engineering, and quantum
                    technology, a new hybrid species emerged: the Prophets.
                  </p>
                  <p>
                    Human in origin but transformed beyond natural limits, they learned to
                    perceive fragments of what has not yet happened.
                  </p>
                  <p>Divided into ten families, each sees tomorrow differently.</p>
                  <p>But the future is never fixed.</p>
                  <p>
                    Every prediction creates a choice.<br />
                    Every choice changes the outcome.
                  </p>
                  <p className="prophets-insight-close">
                    The Prophets do not simply see what comes next.<br />
                    <strong>They shape it.</strong>
                  </p>
                </div>
              </div>

              {/* Right column — cinematic artwork that fades into the frame */}
              <div className="prophets-insight-prophecy-media" aria-hidden="true">
                <img
                  src="/img/prophecy1.webp"
                  alt=""
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                  className="prophets-insight-prophecy-img"
                />
                {/* Soft gradient overlays that blend the artwork into the
                    dark frame on the left / top / bottom edges. */}
                <span className="prophets-insight-prophecy-fade prophets-insight-prophecy-fade--left" />
                <span className="prophets-insight-prophecy-fade prophets-insight-prophecy-fade--top" />
                <span className="prophets-insight-prophecy-fade prophets-insight-prophecy-fade--bottom" />
              </div>
            </div>
          </div>

          {/* ---------------- Panel 2 — Discover the Families ---------------- */}
          <div
            className="prophets-insight-panel prophets-insight-panel--2"
            data-testid="insight-panel-2"
            role="tabpanel"
            aria-labelledby="insight-dot-1"
            aria-hidden={active !== 1}
            style={{ "--family-accent": familyAccent }}
          >
            <div className="prophets-insight-panel-inner">
              <FamilyExplorer onActiveFamilyChange={onActiveFamilyChange} />
            </div>
          </div>

          {/* ---------------- Panel 3 — Reveal Your Prophecy ---------------- */}
          <div
            className="prophets-insight-panel prophets-insight-panel--3"
            data-testid="insight-panel-3"
            role="tabpanel"
            aria-labelledby="insight-dot-2"
            aria-hidden={active !== 2}
          >
            <ProphecyWheel />
          </div>
        </div>
      </div>

      {/* Pagination dots — live under the frame. */}
      <div
        role="tablist"
        aria-label="Prophets Insight pagination"
        data-testid="insight-dots"
        className="prophets-insight-dots"
      >
        {[0, 1, 2].map((i) => (
          <button
            key={i}
            id={`insight-dot-${i}`}
            type="button"
            role="tab"
            aria-selected={i === active}
            aria-label={`Show panel ${i + 1} of 3`}
            data-testid={`insight-dot-${i}`}
            onClick={() => onNavigate(i)}
            className={`prophets-insight-dot ${i === active ? "is-active" : ""}`}
          />
        ))}
      </div>

      {/* MOBILE-ONLY reveal slot — ProphecyWheel portals its result card
          here when window.innerWidth < 768 so the shared frame stays
          compact and the result flows below it in normal document flow. */}
      <div id="prophecy-mobile-reveal-slot" className="prophets-insight-mobile-slot" />
    </section>
  );
}
